import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import StatusBadge from "../../generals/StatusBadge";
import LogicContext from "../../Context/LogicContext";
import { employeeCategoryService } from "../../services/employeeCategory.service";
import EmployeeCategorySideScroll from "./EmployeeCategorySideScroll";
import EmployeeCategoryOverview from "./EmployeeCategoryOverview";
import EmployeeCategoryFormModal from "./EmployeeCategoryFormModal";

function isActive(record) {
  return Number(record?.statusId) === 1;
}

function employeeCategoryToastMessage(message) {
  return /employee category/i.test(message) ? message : `Employee Category: ${message}`;
}

function EmployeeCategoryDetailsPage() {
  const [employeeCategory, setEmployeeCategory] = useState(null);
  const [employeeCategories, setEmployeeCategories] = useState([]);
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const logicContext = useContext(LogicContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const basePath = `/${url[1]}/${url[2]}/${url[3] || "employee-categories"}`;

  useEffect(() => {
    document.title = "Employee Category Details";
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError("");
    Promise.all([
      employeeCategoryService.getEmployeeCategory(id),
      employeeCategoryService.getEmployeeCategories(),
    ])
      .then(([detail, list]) => {
        if (latestRequest.current !== requestId) return;
        setEmployeeCategory(detail);
        setEmployeeCategories(list.employeeCategories);
        setEmployeeTypeOptions(list.employeeTypeOptions);
      })
      .catch((loadError) => {
        if (latestRequest.current !== requestId) return;
        setError(
          loadError?.message
            ? employeeCategoryToastMessage(loadError.message)
            : "Unable to load Employee Category details."
        );
        setEmployeeCategory(null);
      })
      .finally(() => {
        if (latestRequest.current === requestId) setLoading(false);
      });
    return () => {
      latestRequest.current += 1;
    };
  }, [id]);

  function viewEmployeeCategoryDetails(employeeCategoryId) {
    navigate(`${basePath}/${employeeCategoryId}`);
  }

  async function refreshAfterWrite() {
    const [detail, list] = await Promise.all([
      employeeCategoryService.getEmployeeCategory(id),
      employeeCategoryService.getEmployeeCategories(),
    ]);
    setEmployeeCategory(detail);
    setEmployeeCategories(list.employeeCategories);
    setEmployeeTypeOptions(list.employeeTypeOptions);
  }

  async function refreshListAfterWrite() {
    const list = await employeeCategoryService.getEmployeeCategories();
    setEmployeeCategories(list.employeeCategories);
    setEmployeeTypeOptions(list.employeeTypeOptions);
  }

  async function handleEditEmployeeCategory({ name, statusId, employeeTypeId }) {
    setActionLoading("edit");
    try {
      const result = await employeeCategoryService.updateEmployeeCategory(id, name, statusId, employeeTypeId);
      logicContext.showToast(employeeCategoryToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeCategoryToastMessage(
            refreshError?.message || "Unable to refresh Employee Category details."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  async function handleCreateEmployeeCategory({ name, employeeTypeId }) {
    setActionLoading("create");
    try {
      const result = await employeeCategoryService.createEmployeeCategory(name, employeeTypeId);
      logicContext.showToast(employeeCategoryToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeCategoryToastMessage(
            refreshError?.message || "Unable to refresh Employee Categories."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  function handleStatusChange() {
    const action = isActive(employeeCategory) ? "deactivate" : "activate";
    setConfirmAction({
      action: "status",
      title: `Confirm ${action} Employee Category`,
      message: `Are you sure you want to ${action} the Employee Category "${employeeCategory.name}"?`,
    });
  }

  async function applyStatusChange() {
    setActionLoading("status");
    try {
      const result = await employeeCategoryService.setEmployeeCategoryStatus(
        id,
        isActive(employeeCategory) ? 2 : 1
      );
      logicContext.showToast(employeeCategoryToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeCategoryToastMessage(
            refreshError?.message || "Unable to refresh Employee Category details."
          ),
          "error"
        );
      }
    } catch (statusError) {
      logicContext.showToast(
        employeeCategoryToastMessage(
          statusError?.message || "Unable to update Employee Category status."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function handleDeleteEmployeeCategory() {
    setConfirmAction({
      action: "delete",
      title: "Confirm Delete Employee Category",
      message: `The Employee Category "${employeeCategory.name}" will be permanently deleted. Do you want to continue?`,
    });
  }

  async function applyDeleteEmployeeCategory() {
    setActionLoading("delete");
    try {
      const result = await employeeCategoryService.deleteEmployeeCategory(id);
      logicContext.showToast(employeeCategoryToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          employeeCategoryToastMessage(
            refreshError?.message || "Unable to refresh Employee Categories."
          ),
          "error"
        );
      }
      navigate(basePath);
    } catch (deleteError) {
      logicContext.showToast(
        employeeCategoryToastMessage(
          deleteError?.message || "Unable to delete Employee Category."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function confirmPendingAction() {
    const action = confirmAction?.action;
    setConfirmAction(null);
    if (action === "status") return applyStatusChange();
    if (action === "delete") return applyDeleteEmployeeCategory();
  }

  const actionsDisabled = loading || Boolean(actionLoading) || !employeeCategory;

  return (
    <div className="@container">
      <div className="mt-2 flex flex-col @xs:flex-row @xs:items-center @xs:justify-end">
        <div className="mr-6 @xs:mt-2 w-fit flex flex-row items-center px-2.5 py-[2px] border-[1px] border-gray-200 bg-white rounded-full">
          <StatusBadge
            statusId={isActive(employeeCategory) ? 1 : "I"}
            statusName={employeeCategory?.statusName || "-"}
          />
        </div>
        <div className="mt-2 flex flex-row items-center">
          <Tooltip title="Delete Employee Category">
            <button
              onClick={handleDeleteEmployeeCategory}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrashAlt className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Edit Employee Category">
            <button
              onClick={() => setFormMode("edit")}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdEdit className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Add Employee Category">
            <button
              onClick={() => setFormMode("create")}
              disabled={actionsDisabled}
              className="px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPlus className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={handleStatusChange}
          disabled={actionsDisabled}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isActive(employeeCategory) ? "Deactivate Employee Category" : "Activate Employee Category"}
        </button>
      </div>

      <div className="mt-6 w-full flex flex-col @xl:flex-row">
        <div className="my-1 mx-1 w-full @xl:w-[210px] flex-shrink-0 bg-white">
          <EmployeeCategorySideScroll
            employeeCategories={employeeCategories}
            viewEmployeeCategoryDetails={viewEmployeeCategoryDetails}
          />
        </div>
        <div className="mt-10 @xl:mt-0 w-full @xl:ml-4">
          <div className="pb-2 border-b-[1.5px] border-b-gray-200 @xl:px-4 flex flex-row flex-wrap @md:justify-between max-w-3xl">
            <div className="mt-2 mr-2 text-sm font-medium cursor-pointer text-primary">
              Employee Category Overview
            </div>
          </div>
          <div className="mt-2 max-auto max-w-6xl text-filterTextColor">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="h-8 w-1/2 rounded bg-gray-100" />
                <div className="mt-4 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
              </div>
            ) : null}
            {!loading && error ? (
              <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">{error}</div>
            ) : null}
            {!loading && !error && !employeeCategory ? (
              <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">
                Employee Category not found.
              </div>
            ) : null}
            {!loading && !error && employeeCategory ? (
              <EmployeeCategoryOverview employeeCategory={employeeCategory} />
            ) : null}
          </div>
        </div>
      </div>

      {formMode ? (
        <EmployeeCategoryFormModal
          mode={formMode}
          initialValues={
            formMode === "edit"
              ? {
                  name: employeeCategory?.name,
                  employeeTypeId: employeeCategory?.employeeTypeId,
                  statusId: employeeCategory?.statusId,
                }
              : { name: "", employeeTypeId: "", statusId: 1 }
          }
          employeeTypeOptions={employeeTypeOptions}
          onSubmit={
            formMode === "edit"
              ? handleEditEmployeeCategory
              : handleCreateEmployeeCategory
          }
          onClose={() => setFormMode("")}
        />
      ) : null}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="employee-category-confirm-title"
      >
        <DialogTitle id="employee-category-confirm-title">{confirmAction?.title}</DialogTitle>
        <DialogContent>{confirmAction?.message}</DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setConfirmAction(null)}>Cancel Employee Category Action</Button>
          <Button
            variant="contained"
            color={confirmAction?.action === "delete" ? "error" : "primary"}
            onClick={confirmPendingAction}
          >
            Confirm Employee Category Action
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EmployeeCategoryDetailsPage;
